import React from "react";
import { Link } from "react-router-dom";
import { LOGO_URL_TIGHT } from "@/lib/brand";

/**
 * FooterLogoRail — one compact institutional lockup rendered as a
 * full-width block above the content grid. Three logos share the
 * same optical baseline but each carries its own natural aspect
 * ratio and independently tuned height so they read as balanced
 * visual weight rather than equal CSS boxes.
 *
 *   [CrewZNatioN]   [Ministry of Commerce]   [Siyak]
 *
 * No decorative background, halo, card, tile, border, shadow, or
 * pseudo-element. The Ministry SVG renders with `filter: none`.
 * The Siyak transparent PNG carries only a crisp four-direction
 * 0.5 px white edge (no blur radius) to keep its darkest strokes
 * legible on the dark footer — this is a pixel-edge assist, not a
 * glow.
 *
 * Alignment: `justify-start` on EN, `justify-end` on AR. Row is
 * `dir="ltr"` so slot order and artwork are never mirrored on AR.
 */

const MINISTRY_SVG = "/assets/legal/ministry-of-commerce.svg";
const MINISTRY_SVG_WHITE = "/assets/legal/ministry-of-commerce-white.svg";
// Owner-supplied crisp official Siyak asset — used byte-identical
// on the Sponsors panel (ivory ground) and here on the footer.
// The mark's own canvas is part of the institutional lockup and is
// left untouched; no recoloring, filter, drop-shadow, halo, tile,
// box or border is applied. Height is optically tuned so it reads
// at the same visual weight as the CrewZNatioN wordmark and the
// Ministry lockup.
const SIYAK_FOOTER_PNG = "/assets/partners/siyak-alruyah-alalamiyah-footer-dark.png";
const FAHRERS_PNG = "/assets/partners/fahrers-circle.png";
const INCAS_WHITE_SVG = "/logos/incas-white.png";
const CR_PDF = "/assets/documents/masirat-alhayat-commercial-registration-en.pdf";
const SIYAK_URL = "https://siyak.sa/";
const FAHRERS_URL = "https://www.instagram.com/thefahrers?igsh=MWZ1ZHZqdnhsbnMxcg==";
const INCAS_URL = "https://www.incas.sa/";

// Four-direction, zero-blur white pixel-edge assist for Siyak only.
// Zero radius → no fuzziness; each drop-shadow places a 1 px offset
// copy of the alpha silhouette so black strokes gain a crisp outline
// where they touch the black footer.
const SIYAK_EDGE_STYLE = {
    filter:
        "drop-shadow(0.5px 0 0 rgba(255,255,255,0.9)) " +
        "drop-shadow(-0.5px 0 0 rgba(255,255,255,0.9)) " +
        "drop-shadow(0 0.5px 0 rgba(255,255,255,0.9)) " +
        "drop-shadow(0 -0.5px 0 rgba(255,255,255,0.9))",
};

export default function FooterLogoRail({ locale = "en" }) {
    const isAr = locale === "ar";
    const homeHref = isAr ? "/ar" : "/";
    const ministryLabel = isAr
        ? "شهادة السجل التجاري — وزارة التجارة (فتح PDF في تبويب جديد)"
        : "Ministry of Commerce — Commercial Registration Certificate (opens PDF in a new tab)";
    const siyakLabel = isAr
        ? "سياق الرؤية العالمية — فتح siyak.sa في تبويب جديد"
        : "Siyak Alruyah Alalamiyah — opens siyak.sa in a new tab";

    // Individually tuned heights per breakpoint — each logo carries
    // its own natural aspect. Sizes chosen so apparent visual weight
    // is balanced: Ministry runs wider (Arabic+English wording line),
    // Siyak is a stacked composition so its box is taller than
    // CrewZNatioN's horizontal wordmark.
    const crewzImg =
        "h-9 sm:h-12 md:h-14 w-auto object-contain select-none";
    // Ministry: `brightness(0) invert(1)` remaps the vector's dark
    // green ink to solid white without blur or raster processing.
    // Owner explicitly approved white Ministry wording on the dark
    // footer. Enlarged to ensure the Arabic + English wording is
    // clearly readable at footer scale.
    const ministryImg =
        "h-12 sm:h-16 md:h-[80px] w-auto object-contain select-none";
    const siyakImg =
        "h-12 sm:h-[68px] md:h-[88px] w-auto object-contain select-none";
    // Fahrers — circular Instagram community mark. Rendered slightly
    // smaller than Siyak so the circle reads as a compact partner
    // badge, not a fourth institutional lockup. +0.5 px across all
    // breakpoints per owner directive (Feb 2026).
    const fahrersImg =
        "h-[64.5px] sm:h-[72.5px] md:h-[80.5px] w-auto object-contain select-none rounded-full";
    const fahrersLabel = isAr ? "زيارة إنستغرام The Fahrers" : "Visit The Fahrers on Instagram";
    // INCAS — 5th mark, current CrewZNatioN sponsor. Owner-directed
    // placement on the same institutional rail. Height tuned to sit
    // at the same optical weight as Ministry and Siyak, using the
    // white variant for the dark footer.
    const incasImg =
        "h-9 sm:h-11 md:h-[52px] w-auto object-contain select-none";
    const incasLabel = isAr
        ? "INCAS — فتح incas.sa في تبويب جديد"
        : "INCAS — opens incas.sa in a new tab";

    const anchorBase =
        "inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--crewz-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--crewz-bg)] rounded-sm";

    return (
        <div
            className={`flex flex-wrap sm:flex-nowrap items-center gap-2.5 sm:gap-4 md:gap-6 mb-10 md:mb-12 justify-start ${isAr ? "sm:justify-end" : ""}`}
            dir="ltr"
            data-testid={isAr ? "footer-logo-rail-ar" : "footer-logo-rail"}
        >
            <Link
                to={homeHref}
                className={anchorBase}
                aria-label="CrewZNatioN"
                data-testid={isAr ? "footer-brand-ar" : "footer-brand"}
            >
                <img src={LOGO_URL_TIGHT} alt="CrewZNatioN" className={crewzImg} draggable={false} />
            </Link>

            <a
                href={CR_PDF}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ministryLabel}
                title={ministryLabel}
                className={anchorBase}
                data-testid={isAr ? "footer-ministry-ar" : "footer-ministry"}
            >
                <img
                    src={MINISTRY_SVG_WHITE}
                    alt={isAr ? "شعار وزارة التجارة" : "Ministry of Commerce logo"}
                    className={ministryImg}
                    style={{ filter: "none" }}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                />
            </a>

            {/* Siyak Alruyah Alalamiyah — restored as the third
                institutional mark using the crisp official asset
                supplied by the owner. No filter, no drop-shadow, no
                halo, no tile, no box, no border — the mark sits
                directly on the footer ground with its own alpha. */}
            <a
                href={SIYAK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={siyakLabel}
                title={siyakLabel}
                className={anchorBase}
                data-testid={isAr ? "footer-siyak-ar" : "footer-siyak"}
            >
                <img
                    src={SIYAK_FOOTER_PNG}
                    alt={isAr ? "شعار سياق الرؤية العالمية" : "Siyak Alruyah Alalamiyah logo"}
                    className={siyakImg}
                    style={{ filter: "none" }}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                />
            </a>

            {/* The Fahrers — Instagram community partner. Circular
                mark isolated from its white source square via an
                alpha ellipse; no filter, no border, no tile. */}
            <a
                href={FAHRERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={fahrersLabel}
                title={fahrersLabel}
                className={anchorBase}
                data-testid={isAr ? "footer-fahrers-ar" : "footer-fahrers"}
            >
                <img
                    src={FAHRERS_PNG}
                    alt={isAr ? "شعار The Fahrers" : "The Fahrers logo"}
                    className={fahrersImg}
                    style={{ filter: "none" }}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                />
            </a>

            {/* INCAS — 5th mark, current CrewZNatioN sponsor.
                Owner-directed placement on the same institutional
                rail. Uses the white-variant SVG so the wordmark
                reads directly on the dark footer without any halo,
                filter, tile, or box. Logo is never mirrored on
                Arabic — the outer row already sets `dir="ltr"`. */}
            <a
                href={INCAS_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={incasLabel}
                title={incasLabel}
                className={anchorBase}
                data-testid={isAr ? "footer-incas-ar" : "footer-incas"}
            >
                <img
                    src={INCAS_WHITE_SVG}
                    alt="INCAS"
                    className={incasImg}
                    style={{ filter: "none" }}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                />
            </a>
        </div>
    );
}
